import React, { useState, useEffect } from 'react';
import { excellenceItems } from '../mockData';
import { ChevronLeft, ChevronRight, Award } from 'lucide-react';
import axios from 'axios';

const Excellence = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [items, setItems] = useState(excellenceItems);

  // Fetch excellence items from API
  useEffect(() => {
    const fetchExcellence = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.get(`${API}/api/excellence`);
        
        if (response.data && response.data.length > 0) {
          // Map API data to match the format expected by the component
          const formattedItems = response.data
            .filter(item => item.isActive)
            .sort((a, b) => a.order - b.order)
            .map((item, index) => ({
              id: index + 1,
              image: item.image,
              title: item.title,
              description: item.description || ''
            }));
          
          setItems(formattedItems.length > 0 ? formattedItems : excellenceItems);
        }
      } catch (error) {
        console.error('Failed to fetch excellence items:', error);
        // Keep mock data if fetch fails
      }
    };

    fetchExcellence();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || items.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, items.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-20 overflow-hidden" style={{ backgroundColor: '#FFF44F' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase mb-2" style={{ color: '#DC2626' }}>
            <span className="block">OUR</span>
            <span className="block text-black">EXCELLENCE</span>
          </h2>
          <p className="text-black text-sm md:text-base font-light mt-3">
            Certified excellence and safety standards that set us apart
          </p>
        </div>

        {/* Certificates Slider */}
        <div className="relative py-4">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${currentIndex * (270 + 24)}px)`
              }}
            >
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 mr-6"
                  style={{ width: '270px', minWidth: '270px' }}
                >
                  {/* Certificate Image - 25% smaller */}
                  <div className="relative h-36 bg-gray-50 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-contain p-3"
                    />
                    {/* Award Badge */}
                    <div className="absolute top-2 right-2 bg-yellow-400 rounded-full p-1.5 shadow-lg">
                      <Award className="text-black" size={18} />
                    </div>
                  </div>

                  {/* Certificate Info - 25% smaller */}
                  <div className="p-4">
                    <h3 className="text-base font-black text-black mb-1.5 line-clamp-2">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-gray-700 text-xs line-clamp-2">
                        {item.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={prevSlide}
              className="bg-black hover:bg-gray-800 text-yellow-400 p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="bg-black hover:bg-gray-800 text-yellow-400 p-3 rounded-full shadow-lg transform hover:scale-110 transition-all duration-300"
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        {/* Bottom Tagline - 10% smaller and non-bold */}
        <div className="text-center mt-12 md:mt-16">
          <p className="text-black font-light text-base md:text-lg">
            Trusted by thousands <span className="mx-2">•</span> Certified for safety <span className="mx-2">•</span> Committed to excellence
          </p>
        </div>
      </div>
    </section>
  );
};

export default Excellence;